/**
 * 个股详情（重写）。
 *
 * 阶段4：行情 + 新闻全部走后端 /api/*，不再直连第三方。
 *   - 行情：StockApi.quote（复用后端 collectors.get_quote，新浪数据源）
 *   - 订阅：useApiStore 真实 CRUD（create / update / remove）
 *   - 新闻预览：/api/news/preview（只读，与推送内容一致）
 *   - 编辑：SubEditModal（max_per_push / 维度 / 影响度 / token）
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Bell, Check, Wifi, Loader2, Pencil, Newspaper, Globe2, AlertTriangle,
} from "lucide-react";
import SubEditModal from "@/components/SubEditModal";
import { useApiStore } from "@/store/useApiStore";
import { NewsApi, StockApi, type PreviewNewsItem, type StockQuoteResult, ApiError } from "@/services/api";
import { changeColor, changeSign, fmtNum, fmtVolume, fmtFlow, relTime } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function StockDetail() {
  const { code = "" } = useParams();
  const [params] = useSearchParams();
  const mktFromUrl = params.get("mkt") ?? "";
  const nav = useNavigate();

  const { subscriptions, createSub, updateSub, removeSub, refresh, initialized } = useApiStore();

  const [stock, setStock] = useState<StockQuoteResult | null>(null);
  const [news, setNews] = useState<PreviewNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newsError, setNewsError] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  // 取消订阅两步确认（防误触，与订阅中心删除交互一致）
  const [cancelConfirming, setCancelConfirming] = useState(false);

  useEffect(() => {
    if (!initialized) refresh();
  }, [initialized, refresh]);

  // 加载行情 + 新闻预览
  useEffect(() => {
    if (!code || !mktFromUrl) return;
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const q = await StockApi.quote(code, mktFromUrl);
        if (!alive) return;
        setStock(q);
        setLoading(false);

        // 新闻预览（只读）
        setNewsLoading(true);
        setNewsError("");
        try {
          const r = await NewsApi.preview(q.code, q.name, mktFromUrl, 15);
          if (!alive) return;
          setNews(r.news);
        } catch (e) {
          if (!alive) return;
          setNewsError(e instanceof ApiError ? e.message : "新闻加载失败");
        } finally {
          if (alive) setNewsLoading(false);
        }
      } catch (e) {
        if (!alive) return;
        setError(e instanceof ApiError ? e.message : "未找到该股票，或网络请求失败。请检查股票代码或稍后重试。");
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code, mktFromUrl]);

  const sub = subscriptions.find((s) => s.stockCode === code);
  const isSubscribed = Boolean(sub);

  const toggleSub = async () => {
    if (!stock) return;
    if (isSubscribed && !cancelConfirming) {
      setCancelConfirming(true);
      setTimeout(() => setCancelConfirming(false), 3000);
      return;
    }
    setSubBusy(true);
    try {
      if (isSubscribed && sub) {
        await removeSub(sub.stockCode);
      } else {
        await createSub(stock.code, stock.name, mktFromUrl);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "操作失败");
    } finally {
      setSubBusy(false);
      setCancelConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="card p-6 h-40 flex items-center">
          <div className="text-sm text-ink-400 flex items-center gap-2">
            <Wifi size={16} className="text-gold animate-flicker" />
            正在从交易所获取实时行情...
          </div>
        </div>
      </div>
    );
  }

  if (error || !stock) {
    return (
      <div className="p-10 text-center">
        <div className="text-ink-300 mb-3">{error ?? "未找到该股票"}</div>
        <button onClick={() => nav("/")} className="btn-gold">返回总览</button>
      </div>
    );
  }

  const domestic = news.filter((n) => n.sourceType === "domestic");
  const intl = news.filter((n) => n.sourceType === "international");

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto">
      <button
        onClick={() => nav(-1)}
        className="flex items-center gap-1.5 text-xs text-ink-400 hover:text-gold transition-colors"
      >
        <ArrowLeft size={14} /> 返回
      </button>

      {/* 行情头图 */}
      <section className="card p-5 md:p-6 relative overflow-hidden animate-riseIn">
        <div className={cn("absolute top-0 left-0 w-1 h-full", stock.changePct >= 0 ? "bg-bull" : "bg-bear")} />
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h1 className="font-serif text-2xl md:text-3xl text-ink-100">{stock.name}</h1>
              <span className="num text-sm text-ink-400">{stock.code}</span>
              <span className="label-tag text-gold border-gold/40 bg-gold/10">{stock.market}</span>
              {stock.sector && (
                <span className="label-tag text-sky-300 border-sky-400/40 bg-sky-400/10">{stock.sector}</span>
              )}
            </div>
            <div className="flex items-baseline gap-3">
              <span className="num text-4xl text-ink-100">{fmtNum(stock.price)}</span>
              <span className={cn("num text-lg", changeColor(stock.changePct))}>
                {changeSign(stock.change)} ({changeSign(stock.changePct)}{stock.changePct.toFixed(2)}%)
              </span>
              <span className="text-[0.6875rem] text-ink-500">{stock.currency}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-ink-700">
          <Metric label="成交量" value={fmtVolume(stock.volume)} />
          <Metric label="成交额" value={stock.amount ? fmtVolume(stock.amount) : "—"} />
          <Metric
            label="主力净流入"
            value={stock.netFlow ? fmtFlow(stock.netFlow) : "—"}
            valueClass={stock.netFlow ? changeColor(stock.netFlow) : "text-ink-500"}
          />
          <Metric label="所属板块" value={stock.sector || "—"} />
        </div>

        <div className="mt-3 pt-3 border-t border-ink-700 flex items-center gap-2 text-[0.625rem] font-mono text-ink-600">
          <Wifi size={10} className="text-gold" />
          数据源：新浪行情 · 后端 /api/quote 转发
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* 左：新闻预览 */}
        <div className="xl:col-span-2 space-y-6">
          {newsLoading ? (
            <section>
              <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 mb-3 title-bar">
                <Newspaper size={16} className="text-gold" /> 增量情报
              </h2>
              <div className="flex items-center gap-2 text-sm text-ink-400 p-4">
                <Loader2 size={14} className="animate-spin text-gold" />
                正在采集与分析...
              </div>
            </section>
          ) : newsError ? (
            <section className="card p-4 text-sm text-bear">{newsError}</section>
          ) : (
            <>
              {intl.length > 0 && (
                <section>
                  <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 mb-3 title-bar">
                    <Globe2 size={16} className="text-gold" />
                    国际影响
                    <span className="text-xs text-ink-500 font-sans">（DeepSeek 翻译）</span>
                  </h2>
                  <div className="space-y-3">
                    {intl.map((n, i) => (
                      <NewsLine key={`intl-${i}`} n={n} />
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h2 className="font-serif text-lg text-ink-100 flex items-center gap-2 mb-3 title-bar">
                  <Newspaper size={16} className="text-gold" />
                  关联新闻
                  <span className="num text-xs text-ink-500">({domestic.length})</span>
                </h2>
                <div className="space-y-3">
                  {domestic.length === 0 ? (
                    <div className="card p-6 text-center text-sm text-ink-400">暂无增量情报</div>
                  ) : (
                    domestic.map((n, i) => <NewsLine key={`dom-${i}`} n={n} />)
                  )}
                </div>
              </section>
            </>
          )}
        </div>

        {/* 右：订阅面板 */}
        <div className="space-y-6">
          <section className="card p-5">
            <h2 className="font-serif text-base text-ink-100 flex items-center gap-2 mb-4 title-bar">
              <Bell size={15} className="text-gold" />
              推送订阅
            </h2>
            <button
              onClick={toggleSub}
              disabled={subBusy}
              className={cn(
                "w-full py-2.5 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2",
                isSubscribed
                  ? "bg-bear/15 text-bear border border-bear/40 hover:bg-bear/25"
                  : "bg-gold text-ink-950 hover:bg-gold-soft",
                subBusy && "opacity-60",
              )}
            >
              {subBusy ? (
                <Loader2 size={15} className="animate-spin" />
              ) : isSubscribed ? (
                cancelConfirming
                  ? <><AlertTriangle size={15} /> 再点一次，确认取消订阅</>
                  : <><Check size={15} /> 已订阅 · 点击取消</>
              ) : (
                <><Bell size={15} /> 开启推送订阅</>
              )}
            </button>

            {isSubscribed && sub && (
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => setEditing(true)}
                  className="btn-ghost w-full text-xs"
                >
                  <Pencil size={12} /> 编辑推送设置
                </button>
                <div className="text-[0.6875rem] text-ink-500 space-y-1 mt-2">
                  <div>单次上限：<span className="num text-gold">{sub.maxPerPush} 条</span></div>
                  <div>最小影响：<span className="text-gold">
                    {sub.minImpact === "high" ? "高" : sub.minImpact === "medium" ? "中+" : "全部"}
                  </span></div>
                  <div>维度：
                    {(["quote","sector","news","announce","international"] as const)
                      .filter((k) => sub.dimensions[k])
                      .map((k) => (
                        <span key={k} className="label-tag text-gold border-gold/30 bg-gold/5 ml-1">
                          {{quote:"行情",sector:"板块",news:"新闻",announce:"公告",international:"国际"}[k]}
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {editing && sub && (
        <SubEditModal
          sub={sub}
          onClose={() => setEditing(false)}
          onSave={(patch) => updateSub(sub.stockCode, patch).then(() => undefined)}
        />
      )}
    </div>
  );
}

function Metric({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div>
      <div className="text-[0.6875rem] text-ink-500 mb-1">{label}</div>
      <div className={cn("num text-base text-ink-100", valueClass)}>{value}</div>
    </div>
  );
}

function NewsLine({ n }: { n: PreviewNewsItem }) {
  const isIntl = n.sourceType === "international";
  return (
    <article className="card card-hover p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {n.tendency === "bullish" && (
          <span className="label-tag text-bull border-bull/40 bg-bull/10">利多</span>
        )}
        {n.tendency === "bearish" && (
          <span className="label-tag text-bear border-bear/40 bg-bear/10">利空</span>
        )}
        <span className={cn(
          "label-tag",
          isIntl ? "text-sky-300 border-sky-400/40 bg-sky-400/10" : "text-ink-300 border-ink-600 bg-ink-800",
        )}>
          {isIntl && <Globe2 size={10} />}{isIntl ? "国际" : "国内"}
        </span>
        {n.impact && (
          <span className={cn(
            "label-tag",
            n.impact === "high" ? "text-bull border-bull/60 bg-bull/10"
              : n.impact === "medium" ? "text-gold border-gold/60 bg-gold/10"
              : "text-ink-300 border-ink-600 bg-ink-800",
          )}>
            {n.impact === "high" ? "高" : n.impact === "medium" ? "中" : "低"}
          </span>
        )}
        <span className="num text-[0.6875rem] text-ink-500 ml-auto">{relTime(n.publishedAt)}</span>
        <span className="text-[0.6875rem] text-ink-400">· {n.source}</span>
      </div>
      <h3 className="font-serif text-[0.9375rem] leading-snug text-ink-100 mb-1">
        {n.translation || n.title}
      </h3>
      {n.translation && n.translation !== n.title && (
        <p className="font-serif italic text-[0.8125rem] text-ink-400 mb-1">{n.title}</p>
      )}
      {n.summary && <p className="text-[0.8125rem] text-ink-300 mb-1 leading-relaxed">{n.summary}</p>}
      {n.logic && (
        <p className="text-[0.75rem] text-gold/80 border-l-2 border-gold/30 pl-3 leading-relaxed">💡 {n.logic}</p>
      )}
      {n.url && (
        <a href={n.url} target="_blank" rel="noreferrer" className="text-[0.6875rem] text-ink-400 hover:text-gold mt-2 inline-block">
          查看原文 →
        </a>
      )}
    </article>
  );
}

